/**
 * Recent additions — what each of you last put in the dex.
 *
 * The head-to-head says where you stand and the chart says which way it has
 * been going; this says what actually just HAPPENED. Three sprites for the
 * friend, five smaller ones for you underneath as the reference point — the
 * asymmetry is deliberate, because on a friend's page the interesting half is
 * theirs, and yours is there to answer "…and what about me?" without pulling
 * focus.
 *
 * It covers every way a row appears — a live scan, a completed trade, or a
 * manual tick — because from the reader's side those are all just "they added
 * this", and the source badge says which it was.
 *
 * Reads deliberately like the wish list (same tile shape, same badge language)
 * so the two panels either side of the chart are visibly one family. It is NOT
 * the same component: a wish is a want and a catch is a have, and giving them
 * one implementation would mean one of the two lying about the other.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { WishlistSprite } from "@/components/WishlistSprite";
import {
  DEX_VARIANT_META,
  VariantBadge,
  getVariantLabel,
  isDexVariantKey,
  variantAlpha,
  variantColor,
  type DexVariantKey,
} from "@/components/dexVariantVisuals";
import { speciesName } from "@/data/speciesNames";
import { DEX_STATE_EVENT, listRecentCatches } from "@/lib/dexStateStore";
import { DEX_TOTALS_EVENT } from "@/lib/dexQueryRefresh";
import type { PokedexVariant } from "@/lib/pogoPokedexVariantClassifier";

/**
 * How many each side shows.
 *
 * THEIRS is three, at full tile size: on a friend's page the interesting half
 * is theirs, and three big sprites is the whole brief.
 *
 * MINE is five. My row is the reference point — "…and what about me?" — and it
 * was already drawn smaller, so the space three small tiles were using was
 * mostly empty. Five fits on ONE LINE at that size, which is the constraint
 * that matters: a second row of my own catches would out-weigh the friend's
 * three and invert the point of the panel.
 */
const THEIR_SLOTS = 3;
const MY_SLOTS = 5;

interface RecentEntry {
  variant: string;
  dexNumber: number;
  formId: string | null;
  at: number;
  /** Only known for MY OWN rows — the friend's source never crosses. */
  source?: "scan" | "manual" | "trade";
}

/** "just now" / "3h" / "6d" — short enough to sit under a 3-across tile. */
function shortAgo(at: number, now: number): string {
  const mins = Math.floor((now - at) / 60_000);
  if (!Number.isFinite(mins) || mins < 0) return "";
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  return `${Math.floor(days / 30)}mo`;
}

const SOURCE_LABEL: Record<string, string> = {
  scan: "scanned",
  trade: "traded",
  manual: "added",
};

/**
 * The dex-variant mark, perched on the tile's top-right corner.
 *
 * Icon-only and circular: at three tiles across, the labelled pill is wider than
 * the tile it belongs to. The NAME is not dropped though — it goes under the
 * sprite as a full VariantBadge, because "which dex was that catch on" is the
 * question the panel exists to answer and a bare glyph made the reader guess
 * (reported on device 2026-08-12). The corner mark stays because it is legible
 * at a glance from the sprite itself; the badge underneath spells it out.
 *
 * It OVERHANGS the corner (negative inset) so it reads as a mark applied to the
 * sprite rather than a thing competing with it for space inside the frame.
 */
function VariantCorner({ variant, size }: { variant: DexVariantKey; size: "lg" | "sm" }) {
  const Icon = DEX_VARIANT_META[variant].Icon;
  const box = size === "lg" ? 16 : 13;
  return (
    <span
      className="absolute inline-flex items-center justify-center rounded-full"
      style={{
        top: -4,
        right: -4,
        width: box,
        height: box,
        background: variantAlpha(variant, 0.9),
        border: `1px solid ${variantColor(variant)}`,
        color: "var(--surface-sunken)",
      }}
      data-testid={`recent-variant-${variant}`}
      title={`${getVariantLabel(variant)} dex`}
    >
      <Icon className={size === "lg" ? "w-2.5 h-2.5" : "w-2 h-2"} aria-hidden />
    </span>
  );
}

function CatchTile({
  entry,
  size,
  now,
  testId,
}: {
  entry: RecentEntry;
  size: "lg" | "sm";
  now: number;
  testId: string;
}) {
  const ago = shortAgo(entry.at, now);
  return (
    <div className="flex flex-col items-center min-w-0" data-testid={testId}>
      {/* `relative` here, not on the framed box: the badge overhangs, and an
          overflow-clip parent would shave the half that hangs out. */}
      <div className="relative w-full">
        <div
          className="w-full aspect-square rounded-lg flex items-center justify-center overflow-clip p-1"
          style={{
            background: "var(--accent-fill-glow-faint)",
            border: "1px solid var(--accent-fill-glow)",
          }}
        >
          <WishlistSprite
            entry={{
              variant: entry.variant as PokedexVariant,
              dexNumber: entry.dexNumber,
              ...(entry.formId ? { formId: entry.formId } : {}),
              addedAt: entry.at,
            }}
            fill
            ariaLabel={speciesName(entry.dexNumber)}
          />
        </div>
        {isDexVariantKey(entry.variant) && (
          <VariantCorner variant={entry.variant as DexVariantKey} size={size} />
        )}
      </div>
      {/* The dex, NAMED on both rows — "which dex was that catch on" is the
          question this panel exists to answer, and a bare glyph made readers
          guess (device, 2026-08-12).

          The five-across row cannot carry the badge: at that width "Purified"
          is wider than its own tile. It gets the same word in the same colour
          as plain text instead, which fits and says the same thing. */}
      {isDexVariantKey(entry.variant) && (
        size === "lg" ? (
          <span className="mt-1 flex justify-center w-full">
            <VariantBadge variant={entry.variant as DexVariantKey} />
          </span>
        ) : (
          <span
            className="mt-1 w-full text-center text-[8px] font-bold leading-tight truncate"
            style={{ color: variantColor(entry.variant as DexVariantKey) }}
          >
            {getVariantLabel(entry.variant as PokedexVariant)}
          </span>
        )
      )}
      <span
        className={`w-full text-center leading-tight truncate mt-0.5 ${size === "lg" ? "text-[9px]" : "text-[8px]"}`}
        style={{ color: "var(--text-tertiary)" }}
        title={speciesName(entry.dexNumber)}
      >
        {speciesName(entry.dexNumber)}
      </span>
      {/* When and how. The friend's rows carry no source — only the timestamp
          crosses — so the label degrades rather than inventing one. */}
      <span
        className={`w-full text-center leading-tight truncate ${size === "lg" ? "text-[8.5px]" : "text-[8px]"}`}
        style={{ color: "var(--text-ink-faint)" }}
      >
        {[entry.source ? SOURCE_LABEL[entry.source] : null, ago].filter(Boolean).join(" · ")}
      </span>
    </div>
  );
}

/** Placeholder so a half-full row keeps its three-across rhythm. */
function EmptySlot({ size }: { size: "lg" | "sm" }) {
  return (
    <div className="flex flex-col items-center min-w-0" aria-hidden>
      <div
        className="w-full aspect-square rounded-lg"
        style={{ background: "var(--surface-sunken)", border: "1px dashed var(--border-subtle)" }}
      />
      <span className={size === "lg" ? "text-[9px] min-h-[15px]" : "text-[8px]"} />
    </div>
  );
}

function Row({
  entries,
  size,
  now,
  testIdPrefix,
}: {
  entries: RecentEntry[];
  size: "lg" | "sm";
  now: number;
  testIdPrefix: string;
}) {
  const slots = size === "lg" ? THEIR_SLOTS : MY_SLOTS;
  return (
    <div
      // Five across uses the FULL width rather than the old 62% cap: five tiles
      // squeezed into two thirds of the card would be 35px each, and the point
      // of showing more is being able to see them. At full width they are still
      // visibly smaller than the friend's three, which is the intent.
      className={`grid gap-2 ${size === "lg" ? "grid-cols-3" : "grid-cols-5"}`}
      data-testid={`${testIdPrefix}-row`}
    >
      {Array.from({ length: slots }).map((_, i) => {
        const e = entries[i];
        return e ? (
          <CatchTile
            key={`${e.variant}-${e.dexNumber}-${e.formId ?? ""}`}
            entry={e}
            size={size}
            now={now}
            testId={`${testIdPrefix}-${i}`}
          />
        ) : (
          <EmptySlot key={`empty-${i}`} size={size} />
        );
      })}
    </div>
  );
}

export function RecentCatchesPanel({
  friendId,
  friendName,
}: {
  friendId: number;
  friendName: string;
}) {
  // Re-read my own store on every dex event so the panel is never staler than
  // the scan that just finished — same signal the head-to-head listens to.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => setTick((n) => n + 1);
    window.addEventListener(DEX_STATE_EVENT, handler);
    window.addEventListener(DEX_TOTALS_EVENT, handler);
    return () => {
      window.removeEventListener(DEX_STATE_EVENT, handler);
      window.removeEventListener(DEX_TOTALS_EVENT, handler);
    };
  }, []);

  const mine: RecentEntry[] = useMemo(
    () =>
      listRecentCatches(MY_SLOTS).map((e) => ({
        variant: e.variant,
        dexNumber: e.dexNumber,
        formId: e.formId ?? null,
        at: e.updatedAt,
        source: e.source,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tick],
  );

  const url = `/api/friends/${friendId}/recent-catches?limit=${THEIR_SLOTS}`;
  const q = useQuery<{ entries: RecentEntry[] }>({
    queryKey: [url],
    staleTime: 2 * 60 * 1000,
    retry: false,
  });
  const theirs = q.data?.entries ?? [];

  // One clock for the whole panel, captured per render: six tiles each calling
  // Date.now() could straddle a minute and show two different "3m"s for the
  // same instant.
  const now = Date.now();

  return (
    <section className="v2-neon-card-soft p-4 mt-4" data-testid="section-recent-catches">
      <span className="v2-eyebrow" data-testid="eyebrow-recent-catches">
        <span className="v2-eyebrow__dot" aria-hidden />
        Just added
      </span>

      <p className="mt-2 mb-1.5 text-[11px] font-semibold truncate" style={{ color: "var(--text-secondary)" }}>
        {friendName}
      </p>
      {theirs.length === 0 ? (
        <p className="text-[11px]" style={{ color: "var(--text-ink-faint)" }} data-testid="recent-catches-theirs-empty">
          {q.isLoading
            ? "Checking…"
            : q.isError
              ? "Couldn't load their recent catches."
              : `Nothing from ${friendName} yet. This fills in as they scan.`}
        </p>
      ) : (
        <Row entries={theirs} size="lg" now={now} testIdPrefix="recent-catches-theirs" />
      )}

      <p className="mt-3 mb-1.5 text-[11px] font-semibold" style={{ color: "var(--text-secondary)" }}>
        You
      </p>
      {mine.length === 0 ? (
        <p className="text-[11px]" style={{ color: "var(--text-ink-faint)" }} data-testid="recent-catches-mine-empty">
          Nothing added yet. Scan your dex and your five most recent land here.
        </p>
      ) : (
        <Row entries={mine} size="sm" now={now} testIdPrefix="recent-catches-mine" />
      )}
    </section>
  );
}
